import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Subscription, combineLatest} from 'rxjs';
import {Router} from '@angular/router';
import {VoucherService} from '../../services/voucher.service';
import {FSVoucher} from '../../models/FSVoucher';
import {BoxForCheckboxComponent} from '../ui/box-for-checkbox.component';
import {ActionButtonComponent} from '../buttons/action-button.component';
import {LoadingSpinnerComponent} from '../ui/loading-spinner-component';
import {CircleButtonComponent} from '../buttons/circle-button.component';
import {DraggerComponent} from '../ui/dragger.component';
import MoneyUtils, {TimeUtils} from '../../utils/Utils';
import {UrlHistoryService} from '../../services/url.history.service';
import {AppStateService} from '../../services/app-state.service';
import {AppState} from '../../models/AppState';
import {SlideToActivateComponent} from '../ui/slide-to-activate.component';
import {TranslateService} from '../../services/translate.service';
import moment from 'moment';

@Component({
  standalone: true,
  selector: 'app-select-voucher',
  templateUrl: './select-voucher.component.html',
  styleUrls: ['./select-voucher.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    BoxForCheckboxComponent,
    ActionButtonComponent,
    LoadingSpinnerComponent,
    CircleButtonComponent,
    DraggerComponent,
    SlideToActivateComponent
  ]
})
export class SelectVoucherComponent implements OnInit, OnDestroy {
  vouchers: FSVoucher[] = [];
  activeVouchers: FSVoucher[] = [];
  inactiveVouchers: FSVoucher[] = [];
  inactiveVouchersToday: FSVoucher[] = [];
  inactiveVouchersOlder: FSVoucher[] = [];
  displayMode: 'active' | 'today' | 'all' = 'active';
  selectedVouchers: Set<string> = new Set();
  isLoading = true;
  private voucherSubscription?: Subscription;
  private appStateSubscription?: Subscription;
  page = 1;
  vendors: any[] = [];
  selectedVendor: string | null = null;
  private appState?: AppState;

  constructor(
    private voucherService: VoucherService,
    private router: Router,
    private urlHistoryService: UrlHistoryService,
    private appStateService: AppStateService,
    protected translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Combine both active and inactive vouchers
    this.voucherSubscription = combineLatest([
      this.voucherService.observeActiveVouchers(),
      this.voucherService.observeInactiveVouchers()
    ]).subscribe(([activeVouchers, inactiveVouchers]) => {
      // Store active vouchers, sorted by created date (newest first)
      this.activeVouchers = (activeVouchers || []).sort((a, b) => {
        const aTime = a.created?.toMillis() || 0;
        const bTime = b.created?.toMillis() || 0;
        return bTime - aTime;
      });

      // Split inactive vouchers into today (last 12 hours) and older
      const inactive = inactiveVouchers || [];
      this.inactiveVouchersToday = inactive.filter(v => this.isVoucherFromToday(v));
      this.inactiveVouchersOlder = inactive.filter(v => !this.isVoucherFromToday(v));

      // Keep full list for backward compatibility
      this.inactiveVouchers = inactive;

      this.updateDisplayedVouchers();
      this.isLoading = false;
    });

    this.appStateSubscription = this.appStateService.observeAppState().subscribe((appState: AppState) => {
      this.appState = appState;
      if (appState?.cfg?.voucher?.vendors) {
        this.vendors = appState.cfg.voucher.vendors;
      }
    });
  }

  ngOnDestroy(): void {
    this.voucherSubscription?.unsubscribe();
    this.appStateSubscription?.unsubscribe();
  }

  toggleVoucher(voucherId: string): void {
    // Find the voucher to check if it's active
    const voucher = this.vouchers.find(v => v.voucher_id === voucherId);
    if (!voucher || !voucher.active) {
      return; // Don't allow selection of inactive vouchers
    }

    if (this.selectedVouchers.has(voucherId)) {
      this.selectedVouchers.delete(voucherId);
    } else {
      this.selectedVouchers.add(voucherId);
    }
  }

  isSelected(voucherId: string): boolean {
    return this.selectedVouchers.has(voucherId);
  }

  handleVoucherClick(voucher: FSVoucher): void {
    if (voucher.active) {
      this.toggleVoucher(voucher.voucher_id);
    } else {
      // Navigate to voucher details/glance view for inactive vouchers
      const venueId = this.appState?.venueId;
      this.router.navigateByUrl(`${venueId}/glance/${voucher.voucher_id}`, { skipLocationChange: true });
    }
  }

  formatPrice(priceCent: number): string {
    return MoneyUtils.formatCent(priceCent);
  }

  displayPriceCent(voucher: FSVoucher): number {
    return MoneyUtils.voucherPriceCent(this.appState?.venueId, voucher);
  }

  nextPage(): void {
    if (this.page === 1 && this.getSelectedCount() > 0) {
      // Skip page 2 if no vendors are available
      if (!this.hasSelectableVendors()) {
        this.page = 3;
      } else {
        this.page = 2;
      }
    } else if (this.page === 2 && this.selectedVendor) {
      this.page = 3;
    }
  }

  activateVouchers(): void {
    const selectedVouchersList = this.vouchers.filter(v => this.selectedVouchers.has(v.voucher_id));
    console.log('Activating vouchers:', selectedVouchersList);
    console.log('Selected vendor:', this.selectedVendor);
    // TODO: Implement voucher activation logic with selected vendor
  }

  getSelectedCount(): number {
    return this.selectedVouchers.size;
  }

  getSelectedVendorName(): string {
    const vendor = this.vendors.find(v => v.id === this.selectedVendor);
    return vendor ? vendor.name : '';
  }

  getButtonText(): string {
    if (this.page === 1) {
      const count = this.getSelectedCount();
      if (count === 0) {
        return 'Nästa';
      } else if (count === 1) {
        return `Nästa`;
      } else {
        return `Nästa - ${count} biljetter`;
      }
    } else if (this.page === 2) {
      const s = this.getSelectedVendorName();
      if (s.length === 0) {
        return 'Nästa';
      }
      return `Nästa - ${s}`;
    } else {
      return 'Aktivera';
    }
  }

  onActionButtonClick(): void {
    if (this.page === 1 || this.page === 2) {
      this.nextPage();
    } else if (this.page === 3) {
      this.activateVouchers();
    }
  }

  getSelectedVouchers(): FSVoucher[] {
    return this.vouchers.filter(v => this.selectedVouchers.has(v.voucher_id));
  }

  getGroupedVouchers(): { count: number; name: string; message: string; price_cent: number }[] {
    const selectedVouchers = this.getSelectedVouchers();
    const grouped = new Map<string, { count: number; voucher: FSVoucher }>();

    for (const voucher of selectedVouchers) {
      const key = `${voucher.name}_${voucher.price_cent}`;
      if (grouped.has(key)) {
        grouped.get(key)!.count++;
      } else {
        grouped.set(key, { count: 1, voucher });
      }
    }

    return Array.from(grouped.values()).map(({ count, voucher }) => ({
      count,
      name: voucher.name,
      message: voucher.message,
      price_cent: this.displayPriceCent(voucher)
    }));
  }

  getTotalPrice(): string {
    const total = this.getSelectedVouchers().reduce((sum, v) => sum + this.displayPriceCent(v), 0);
    return MoneyUtils.formatCent(total);
  }

  public backClicked() {
    console.log("Back clicked, page:", this.page);
    if (this.page === 1) {
      this.urlHistoryService.navigateBack();
    } else if (this.page === 2) {
      this.page = 1;
      this.selectedVendor = null;
    } else if (this.page === 3) {
      // If no vendors, go back to page 1; otherwise go to page 2
      if (!this.hasSelectableVendors()) {
        this.page = 1;
      } else {
        this.page = 2;
      }
    }
  }

  public closeClicked() {
    console.log("Close clicked");
    const venueId = this.appState?.venueId;
    this.router.navigateByUrl(`${venueId}/menu/food`);
  }

  getString(s: string, pc: string = "normal") {
    return this.translate.single(s, pc);
  }

  getActivationText(voucher: FSVoucher): string | null {
    if (!voucher.active && voucher.applied) {
      const appliedMoment = moment(voucher.applied.toDate());
      let name= 'Aktiverad ' + TimeUtils.getRelativeDayStringTimeDate(appliedMoment);
      if (voucher.vendor_id != null) {
        const vendor = this.vendors.find(v => v.id === voucher.vendor_id);
        if (vendor) {
          name += ' hos ' + vendor.name;
        } else {
          name += ' hos ' + voucher.vendor_id;
        }
      }
      return name;
    }
    return null;
  }

  isVoucherFromToday(voucher: FSVoucher): boolean {
    if (!voucher.applied) return false;
    const now = Date.now();
    const twelveHoursAgo = now - (12 * 60 * 60 * 1000);
    const appliedTime = voucher.applied.toMillis();
    return appliedTime >= twelveHoursAgo;
  }

  updateDisplayedVouchers(): void {
    let displayVouchers: FSVoucher[] = [];

    switch(this.displayMode) {
      case 'active':
        // Show only active vouchers
        displayVouchers = [...this.activeVouchers].sort((a, b) => {
          const aTime = a.created?.toMillis() || 0;
          const bTime = b.created?.toMillis() || 0;
          return bTime - aTime; // Newest first
        });
        break;

      case 'today':
        // Show active first (sorted by created), then today's inactive (sorted by applied)
        const sortedTodayInactive = [...this.inactiveVouchersToday].sort((a, b) => {
          const aTime = a.applied?.toMillis() || 0;
          const bTime = b.applied?.toMillis() || 0;
          return bTime - aTime; // Newest first
        });
        const sortedActiveToday = [...this.activeVouchers].sort((a, b) => {
          const aTime = a.created?.toMillis() || 0;
          const bTime = b.created?.toMillis() || 0;
          return bTime - aTime; // Newest first
        });
        displayVouchers = [...sortedActiveToday, ...sortedTodayInactive];
        break;

      case 'all':
        // Show active first (sorted by created), then all inactive (sorted by applied)
        const allInactive = [...this.inactiveVouchersToday, ...this.inactiveVouchersOlder];
        const sortedAllInactive = allInactive.sort((a, b) => {
          const aTime = a.applied?.toMillis() || 0;
          const bTime = b.applied?.toMillis() || 0;
          return bTime - aTime; // Newest first
        });
        const sortedActiveAll = [...this.activeVouchers].sort((a, b) => {
          const aTime = a.created?.toMillis() || 0;
          const bTime = b.created?.toMillis() || 0;
          return bTime - aTime; // Newest first
        });
        displayVouchers = [...sortedActiveAll, ...sortedAllInactive];
        break;
    }

    this.vouchers = displayVouchers;
  }

  cycleDisplayMode(): void {
    if (this.displayMode === 'active') {
      // If we have vouchers from today, go to 'today' mode
      if (this.inactiveVouchersToday.length > 0) {
        this.displayMode = 'today';
      } 
      // If we only have older vouchers (no today vouchers), go directly to 'all' mode
      else if (this.inactiveVouchersOlder.length > 0) {
        this.displayMode = 'all';
      }
    } else if (this.displayMode === 'today' && this.inactiveVouchersOlder.length > 0) {
      this.displayMode = 'all';
    } else {
      this.displayMode = 'active';
    }
    this.updateDisplayedVouchers();
  }

  toggleShowInactive(): void {
    // Keep for backward compatibility, now uses cycleDisplayMode
    this.cycleDisplayMode();
  }

  getToggleButtonText(): string {
    if (this.displayMode === 'active') {
      // If we only have older vouchers (no today vouchers), show "Visa alla biljetter"
      // Otherwise show "Visa tidigare biljetter"
      if (this.inactiveVouchersToday.length === 0 && this.inactiveVouchersOlder.length > 0) {
        return 'Visa alla biljetter';
      }
      return 'Visa tidigare biljetter';
    } else if (this.displayMode === 'today') {
      // Show "Visa alla" only if there are older vouchers, otherwise show "Dölj"
      return this.inactiveVouchersOlder.length > 0 ? 'Visa alla biljetter' : 'Dölj tidigare biljetter';
    } else if (this.displayMode === 'all') {
      return 'Dölj tidigare biljetter';
    }
    return '';
  }

  shouldShowToggleButton(): boolean {
    return this.inactiveVouchersToday.length > 0 || this.inactiveVouchersOlder.length > 0;
  }

  hasSelectableVendors(): boolean {
    return this.vendors != null && this.vendors.length > 0;
  }

  onSliderActivated() {
    const venueId = this.appState?.venueId;
    const voucherIds = Array.from(this.selectedVouchers).join(',');

    // Add vendorId as query param if it's set
    if (this.selectedVendor) {
      this.router.navigate([`${venueId}/glance/${voucherIds}`], {
        skipLocationChange: true,
        queryParams: { vendorId: this.selectedVendor }
      });
    } else {
      this.router.navigateByUrl(`${venueId}/glance/${voucherIds}`, { skipLocationChange: true });
    }
  }
}
